import { format } from "date-fns";
import { Clock, MapPin, Tv, Trophy, Calendar, Bell, BellOff, TrendingUp, DollarSign, ExternalLink, Timer, RefreshCw, Goal, User } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ScheduledGame } from "@/hooks/useSchedule";
import { useGameNotifications } from "@/hooks/useGameNotifications";
import { useLiveOdds, type OddsEvent } from "@/hooks/useLiveOdds";
import { useLiveGameRefresh } from "@/hooks/useLiveGameRefresh";
import { useGameEvents } from "@/hooks/useGameEvents";
import { useAuth } from "@/contexts/AuthContext";
import { useMemo, useState, useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { olympicsBroadcasts, BROADCAST_DISCLAIMER } from "@/data/olympicsBroadcasts";
import PlayerProfileModal from "@/components/PlayerProfileModal";

interface GameDetailsModalProps {
  game: ScheduledGame | null;
  isOpen: boolean;
  onClose: () => void;
  /** If true, show Olympics-style regional broadcast info instead of league channels */
  isOlympics?: boolean;
  /** Optional stage/group info */
  stage?: string;
}

/** Safely access a period's score, returns null if period data is missing */
const getPeriodScore = (
  periods: ScheduledGame["periods"] | undefined | null,
  period: "first" | "second" | "third" | "overtime" | "penalties",
  side: "home" | "away"
): number | null => {
  try {
    return periods?.[period]?.[side] ?? null;
  } catch {
    return null;
  }
};

/** Countdown hook for upcoming games */
function useCountdown(targetTimestamp: number | null) {
  const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number; seconds: number } | null>(null);

  useEffect(() => {
    if (!targetTimestamp || targetTimestamp <= 0) {
      setTimeLeft(null);
      return;
    }

    const calculate = () => {
      const now = Date.now();
      const target = targetTimestamp * 1000; // convert seconds to ms
      const diff = target - now;
      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setTimeLeft({ days, hours, minutes, seconds });
    };

    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [targetTimestamp]);

  return timeLeft;
}

/** Regional broadcast info for general leagues */
const getRegionalBroadcasts = (league: string) => {
  const info: { flag: string; label: string; channels: string }[] = [];
  
  switch (league) {
    case "NHL":
    case "AHL":
    case "ECHL":
      info.push(
        { flag: "🇺🇸", label: "USA", channels: "ESPN+ / TNT / NHL Network" },
        { flag: "🇨🇦", label: "Canada", channels: "TSN / Sportsnet" },
        { flag: "🇪🇺", label: "Europe", channels: "Viaplay / Nova Sport" },
      );
      break;
    case "SHL":
    case "Allsvenskan":
      info.push(
        { flag: "🇸🇪", label: "Sweden", channels: "C More / TV4" },
        { flag: "🇪🇺", label: "Europe", channels: "Discovery+" },
      );
      break;
    case "Liiga":
      info.push(
        { flag: "🇫🇮", label: "Finland", channels: "Elisa Viihde / C More" },
        { flag: "🇪🇺", label: "Europe", channels: "Discovery+" },
      );
      break;
    case "KHL":
      info.push(
        { flag: "🇷🇺", label: "Russia", channels: "Match TV / KHL TV" },
      );
      break;
    case "DEL":
      info.push(
        { flag: "🇩🇪", label: "Germany", channels: "MagentaSport" },
      );
      break;
    case "NL":
      info.push(
        { flag: "🇨🇭", label: "Switzerland", channels: "MySports" },
      );
      break;
    case "Extraliga":
      info.push(
        { flag: "🇨🇿", label: "Czechia", channels: "O2 TV Sport / Nova Sport" },
      );
      break;
    case "PWHL":
      info.push(
        { flag: "🇺🇸", label: "USA", channels: "ESPN+" },
        { flag: "🇨🇦", label: "Canada", channels: "TSN" },
      );
      break;
    default:
      info.push(
        { flag: "📺", label: "Check", channels: "Local broadcaster" },
      );
  }
  return info;
};

const GameDetailsModal = ({ game: gameProp, isOpen, onClose, isOlympics = false, stage }: GameDetailsModalProps) => {
  const { subscription } = useAuth();
  const [selectedPlayerName, setSelectedPlayerName] = useState<string | null>(null);
  const { addReminder, removeReminder, hasReminder, isSupported } = useGameNotifications();
  const { data: oddsData } = useLiveOdds();

  // Live refresh: polls every 30s while game is in progress (disabled for Olympics – they use their own live feed)
  const { data: refreshedGame, dataUpdatedAt, isFetching: isRefreshing } = useLiveGameRefresh(gameProp, isOpen && !isOlympics);
  const game = refreshedGame ?? gameProp;

  // Game events (goals) — auto-refresh gated to Pro/Elite
  const canAutoRefreshEvents = subscription.tier === "pro" || subscription.tier === "elite";
  const { events: goalEvents, isLoading: eventsLoading, error: eventsError } = useGameEvents(
    game?.id ?? null,
    isOpen && !!(game?.isLive || game?.isFinished),
    game?.isLive ?? false,
    canAutoRefreshEvents
  );

  // Track last update for display
  const lastUpdated = game?.isLive && dataUpdatedAt ? new Date(dataUpdatedAt) : null;

  // Flash animation on score change
  const prevScoreRef = useRef<string>("");
  const [scoreFlash, setScoreFlash] = useState(false);
  useEffect(() => {
    if (!game) return;
    const scoreKey = `${game.homeScore}-${game.awayScore}`;
    if (prevScoreRef.current && prevScoreRef.current !== scoreKey) {
      setScoreFlash(true);
      const timer = setTimeout(() => setScoreFlash(false), 1500);
      return () => clearTimeout(timer);
    }
    prevScoreRef.current = scoreKey;
  }, [game?.homeScore, game?.awayScore]);

  const countdown = useCountdown(game?.isScheduled ? game.timestamp : null);

  const matchingOdds = useMemo(() => {
    if (!game || !oddsData) return null;
    const normalizeTeam = (name: string) => name.toLowerCase().replace(/[^a-z]/g, '');
    const homeNorm = normalizeTeam(game.homeTeam);
    const awayNorm = normalizeTeam(game.awayTeam);
    return oddsData.find((odds: OddsEvent) => {
      const oddsHomeNorm = normalizeTeam(odds.homeTeam);
      const oddsAwayNorm = normalizeTeam(odds.awayTeam);
      return (
        (oddsHomeNorm.includes(homeNorm) || homeNorm.includes(oddsHomeNorm)) &&
        (oddsAwayNorm.includes(awayNorm) || awayNorm.includes(oddsAwayNorm))
      ) || (
        (oddsHomeNorm.includes(awayNorm) || awayNorm.includes(oddsHomeNorm)) &&
        (oddsAwayNorm.includes(homeNorm) || homeNorm.includes(oddsAwayNorm))
      );
    });
  }, [game, oddsData]);

  if (!game) return null;

  const gameDate = game.date ? new Date(game.date + "T00:00:00") : null;
  const isValidDate = gameDate && !isNaN(gameDate.getTime());
  const formattedDate = isValidDate
    ? format(gameDate, "EEEE, MMMM d, yyyy")
    : "Date unavailable";

  const hasPeriodScores = !!(
    game.periods &&
    (getPeriodScore(game.periods, "first", "home") !== null ||
     getPeriodScore(game.periods, "second", "home") !== null ||
     getPeriodScore(game.periods, "third", "home") !== null)
  );

  const hasOvertime = getPeriodScore(game.periods, "overtime", "home") !== null;
  const hasShootout = getPeriodScore(game.periods, "penalties", "home") !== null;

  const isReminderSet = hasReminder(game.id);
  const canSetReminder = game.isScheduled && isSupported && game.timestamp > Date.now();

  const handleReminderToggle = () => {
    if (isReminderSet) {
      removeReminder(game.id);
    } else {
      addReminder(
        game.id,
        game.homeTeam,
        game.awayTeam,
        game.time,
        game.date,
        game.league,
        game.timestamp * 1000
      );
    }
  };

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, " ");

  return (
    <>
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-card border-border p-0">
        <ScrollArea className="h-full">
          <div className="p-6">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary">{game.league}</Badge>
                {stage && (
                  <Badge variant="outline">{stage}</Badge>
                )}
                <span className="text-muted-foreground text-sm font-normal">
                  {game.leagueName}
                </span>
              </SheetTitle>
            </SheetHeader>

            {/* Teams and Score */}
            <div className="flex items-center justify-between py-6">
              <div className="flex flex-col items-center gap-2 flex-1">
                {game.awayLogo ? (
                  <img src={game.awayLogo} alt={game.awayTeam} className="w-16 h-16 object-contain"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                ) : (
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center text-lg font-bold">
                    {game.awayAbbr}
                  </div>
                )}
                <p className="font-semibold text-foreground text-center text-sm">{game.awayTeam}</p>
              </div>

              <div className="flex flex-col items-center px-4">
              {game.isLive ? (
                  <>
                    <Badge variant="destructive" className="animate-pulse mb-2 gap-1.5">
                      <span className="w-2 h-2 bg-white rounded-full inline-block" />
                      LIVE
                    </Badge>
                    <span className="text-xs font-semibold text-primary mb-1">
                      {(() => {
                        const s = (game.statusShort || "").toUpperCase();
                        if (s === "1P") return "1st Period";
                        if (s === "2P") return "2nd Period";
                        if (s === "3P") return "3rd Period";
                        if (s === "OT") return "Overtime";
                        if (s === "BT" || s === "P") return "Shootout";
                        if (s === "HT" || s === "INT") return "Intermission";
                        return game.status || s;
                      })()}
                    </span>
                    <div className={`flex items-center gap-3 text-3xl font-bold transition-colors duration-500 ${scoreFlash ? 'text-primary' : ''}`}>
                      <span>{game.awayScore ?? 0}</span>
                      <span className="text-muted-foreground">-</span>
                      <span>{game.homeScore ?? 0}</span>
                    </div>
                    {/* Live refresh indicator */}
                    <div className="flex items-center gap-1.5 mt-2 text-[10px] text-muted-foreground">
                      <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin text-primary' : ''}`} />
                      <span>
                        {lastUpdated
                          ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
                          : 'Auto-refreshing every 15s'}
                      </span>
                    </div>
                  </>
                ) : game.isFinished ? (
                  <>
                    <Badge variant="secondary" className="mb-2">
                      {game.statusShort === "AOT" ? "FINAL/OT" :
                       game.statusShort === "AP" ? "FINAL/SO" : "FINAL"}
                    </Badge>
                    <div className="flex items-center gap-3 text-3xl font-bold">
                      <span>{game.awayScore ?? 0}</span>
                      <span className="text-muted-foreground">-</span>
                      <span>{game.homeScore ?? 0}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <Badge variant="outline" className="mb-2">UPCOMING</Badge>
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-primary">
                        Puck Drop
                      </span>
                      <div className="flex items-center gap-2 text-2xl font-bold text-primary">
                        <Clock className="h-5 w-5" />
                        <span>{game.time}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{userTimezone}</span>
                    </div>
                    {canSetReminder && (
                      <Button variant={isReminderSet ? "secondary" : "default"} size="sm" className="mt-3 gap-2" onClick={handleReminderToggle}>
                        {isReminderSet ? (<><BellOff className="h-4 w-4" />Cancel Reminder</>) : (<><Bell className="h-4 w-4" />Remind Me</>)}
                      </Button>
                    )}
                  </>
                )}
              </div>

              <div className="flex flex-col items-center gap-2 flex-1">
                {game.homeLogo ? (
                  <img src={game.homeLogo} alt={game.homeTeam} className="w-16 h-16 object-contain"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                ) : (
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center text-lg font-bold">
                    {game.homeAbbr}
                  </div>
                )}
                <p className="font-semibold text-foreground text-center text-sm">{game.homeTeam}</p>
              </div>
            </div>

            {/* Countdown for upcoming games */}
            {game.isScheduled && countdown && (
              <>
                <div className="py-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Timer className="h-4 w-4 text-primary" />
                    Countdown to Puck Drop
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: "Days", value: countdown.days },
                      { label: "Hours", value: countdown.hours },
                      { label: "Mins", value: countdown.minutes },
                      { label: "Secs", value: countdown.seconds },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-muted/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-primary tabular-nums">{String(value).padStart(2, "0")}</p>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <Separator />
              </>
            )}

            <Separator />

            {/* Period Scores */}
            {(game.isLive || game.isFinished) && hasPeriodScores && (
              <>
                <div className="py-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-primary" />
                    Period Scores
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50">
                        <TableHead className="w-[120px]">Team</TableHead>
                        <TableHead className="text-center">1st</TableHead>
                        <TableHead className="text-center">2nd</TableHead>
                        <TableHead className="text-center">3rd</TableHead>
                        {hasOvertime && <TableHead className="text-center">OT</TableHead>}
                        {hasShootout && <TableHead className="text-center">SO</TableHead>}
                        <TableHead className="text-center font-bold">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow className="border-border/50">
                        <TableCell className="font-medium">{game.awayAbbr}</TableCell>
                        <TableCell className="text-center">{getPeriodScore(game.periods, "first", "away") ?? "-"}</TableCell>
                        <TableCell className="text-center">{getPeriodScore(game.periods, "second", "away") ?? "-"}</TableCell>
                        <TableCell className="text-center">{getPeriodScore(game.periods, "third", "away") ?? "-"}</TableCell>
                        {hasOvertime && <TableCell className="text-center">{getPeriodScore(game.periods, "overtime", "away") ?? "-"}</TableCell>}
                        {hasShootout && <TableCell className="text-center">{getPeriodScore(game.periods, "penalties", "away") ?? "-"}</TableCell>}
                        <TableCell className="text-center font-bold">{game.awayScore ?? 0}</TableCell>
                      </TableRow>
                      <TableRow className="border-border/50">
                        <TableCell className="font-medium">{game.homeAbbr}</TableCell>
                        <TableCell className="text-center">{getPeriodScore(game.periods, "first", "home") ?? "-"}</TableCell>
                        <TableCell className="text-center">{getPeriodScore(game.periods, "second", "home") ?? "-"}</TableCell>
                        <TableCell className="text-center">{getPeriodScore(game.periods, "third", "home") ?? "-"}</TableCell>
                        {hasOvertime && <TableCell className="text-center">{getPeriodScore(game.periods, "overtime", "home") ?? "-"}</TableCell>}
                        {hasShootout && <TableCell className="text-center">{getPeriodScore(game.periods, "penalties", "home") ?? "-"}</TableCell>}
                        <TableCell className="text-center font-bold">{game.homeScore ?? 0}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
                <Separator />
              </>
            )}

            {/* Scoring Summary */}
            <>
              <div className="py-4">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Goal className="h-4 w-4 text-primary" />
                  Scoring Summary
                  {game.isLive && canAutoRefreshEvents && (
                    <span className="ml-auto flex items-center gap-1.5 text-[10px] text-primary/80">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                      Live • updates every 30s
                    </span>
                  )}
                  {game.isLive && !canAutoRefreshEvents && (
                    <Badge variant="outline" className="text-[10px] ml-auto opacity-60">Pro/Elite: live updates</Badge>
                  )}
                </h4>
                {game.isScheduled ? (
                  <p className="text-xs text-muted-foreground italic">
                    Scoring summary available once game starts
                  </p>
                ) : eventsLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <RefreshCw className="h-3.5 w-3.5 animate-spin text-primary" />
                    <span>Loading goal events…</span>
                  </div>
                ) : eventsError && goalEvents.length === 0 ? (
                  <p className="text-xs text-destructive/80 italic">
                    Unable to load goal events — please try again later
                  </p>
                ) : goalEvents.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    No goals yet – check back after puck drop
                  </p>
                ) : (
                    <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                      {(() => {
                        let runningHome = 0;
                        let runningAway = 0;
                        return goalEvents.map((event, idx) => {
                          const isHome = event.team?.name === game.homeTeam;
                          const teamLogo = isHome ? game.homeLogo : game.awayLogo;
                          if (isHome) runningHome++; else runningAway++;

                          // Period label: normalise to P1/P2/P3/OT/SO
                          const raw = (event.period || "").replace(/\s/g, "");
                          const periodTag =
                            raw === "1" || raw === "P1" || raw.toLowerCase() === "1st" ? "P1"
                            : raw === "2" || raw === "P2" || raw.toLowerCase() === "2nd" ? "P2"
                            : raw === "3" || raw === "P3" || raw.toLowerCase() === "3rd" ? "P3"
                            : raw.toUpperCase().startsWith("OT") ? "OT"
                            : raw.toUpperCase().startsWith("SO") || raw.toUpperCase() === "PENALTIES" ? "SO"
                            : raw || "?";
                          const periodColor =
                            periodTag === "P1" ? "bg-blue-500/20 text-blue-400 border-blue-500/30"
                            : periodTag === "P2" ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                            : periodTag === "P3" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                            : "bg-purple-500/20 text-purple-400 border-purple-500/30";

                          const timeStr = event.minute || event.time || "";
                          const scorer = event.players?.[0] || "Unknown";
                          const assistStr = event.assists && event.assists.length > 0
                            ? ` (${event.assists.join(", ")})`
                            : "";

                          return (
                            <div
                              key={idx}
                              className="flex items-start gap-2.5 p-2.5 rounded-lg bg-muted/50 border border-border/50"
                            >
                              {/* Period badge */}
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 mt-0.5 ${periodColor}`}>
                                {periodTag}
                              </span>

                              {/* Team logo */}
                              {teamLogo && (
                                <img src={teamLogo} alt="" className="w-5 h-5 object-contain shrink-0 mt-0.5"
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                              )}

                              {/* Goal info */}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-foreground leading-snug">
                                  <span className="text-muted-foreground font-mono text-xs">{timeStr}</span>
                                  <span className="text-muted-foreground/50 mx-1">–</span>
                                   <button
                                    type="button"
                                    className="font-bold text-primary underline underline-offset-2 hover:text-primary/80 transition-colors cursor-pointer"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setSelectedPlayerName(scorer);
                                    }}
                                    title={`View ${scorer}'s profile`}
                                  >
                                    {scorer}
                                  </button>
                                  {assistStr && (
                                    <span className="text-muted-foreground/70 text-xs">
                                      {" ("}
                                      {event.assists!.map((assist, aIdx) => (
                                        <span key={aIdx}>
                                          {aIdx > 0 && ", "}
                                          <button
                                            type="button"
                                            className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors cursor-pointer"
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setSelectedPlayerName(assist);
                                            }}
                                            title={`View ${assist}'s profile`}
                                          >
                                            {assist}
                                          </button>
                                        </span>
                                      ))}
                                      {")"}
                                    </span>
                                  )}
                                </div>
                                {event.comments && (
                                  <p className="text-[10px] text-muted-foreground/50 italic mt-0.5">{event.comments}</p>
                                )}
                              </div>

                              {/* Running score */}
                              <span className="text-xs font-bold text-foreground tabular-nums shrink-0 mt-0.5">
                                {runningAway}–{runningHome}
                              </span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
                <Separator />
              </>

            {/* Betting Odds */}
            {matchingOdds && (matchingOdds.homeOdds || matchingOdds.awayOdds) && (
              <>
                <Separator />
                <div className="py-4">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-primary" />
                    Betting Odds
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground mb-1">{game.awayAbbr} Win</p>
                      <p className="text-lg font-bold text-foreground">{matchingOdds.awayOdds?.toFixed(2) || '-'}</p>
                    </div>
                    {matchingOdds.drawOdds && (
                      <div className="bg-muted/50 rounded-lg p-3 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Draw</p>
                        <p className="text-lg font-bold text-foreground">{matchingOdds.drawOdds.toFixed(2)}</p>
                      </div>
                    )}
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground mb-1">{game.homeAbbr} Win</p>
                      <p className="text-lg font-bold text-foreground">{matchingOdds.homeOdds?.toFixed(2) || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Odds from {matchingOdds.league || 'BetsAPI'}
                    </p>
                    <Button variant="outline" size="sm" className="gap-1.5 text-xs"
                      onClick={() => {
                        const searchQuery = encodeURIComponent(`${game.awayTeam} vs ${game.homeTeam} betting odds`);
                        window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank');
                      }}>
                      <ExternalLink className="h-3 w-3" />
                      Find Bets
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Broadcast / Where to Watch */}
            <Separator />
            <div className="py-4">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Tv className="h-4 w-4 text-primary" />
                Broadcast / Watch
              </h4>

              {isOlympics ? (
                /* Olympics: full regional broadcast grid */
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {olympicsBroadcasts.map((b) => (
                      <div key={b.country} className="flex items-start gap-2 p-2.5 rounded-lg bg-muted/50 border border-border/50">
                        <span className="text-sm mt-0.5">{b.flag}</span>
                        <div>
                          <span className="text-xs font-semibold text-foreground">{b.country}</span>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {b.channels.map((ch) =>
                              ch.url ? (
                                <a key={ch.name} href={ch.url} target="_blank" rel="noopener noreferrer"
                                  className="text-[11px] text-primary hover:underline font-medium">
                                  {ch.name}
                                </a>
                              ) : (
                                <span key={ch.name} className="text-[11px] text-muted-foreground">{ch.name}</span>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground italic">
                    {BROADCAST_DISCLAIMER}
                  </p>
                </div>
              ) : (
                /* Regular leagues: regional summary */
                <div className="space-y-2">
                  {getRegionalBroadcasts(game.league).map(({ flag, label, channels }) => (
                    <div key={label} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border border-border/50">
                      <span className="text-sm">{flag}</span>
                      <span className="text-xs font-semibold text-foreground min-w-[60px]">{label}</span>
                      <span className="text-xs text-muted-foreground">{channels}</span>
                    </div>
                  ))}
                  {game.tvChannels && game.tvChannels.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {game.tvChannels.map((channel) => (
                        <Badge key={channel} variant="outline" className="text-xs">{channel}</Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground italic mt-2">
                    Exact channel may vary – check your local provider for assignment.
                  </p>
                </div>
              )}
            </div>

            {/* Game Info */}
            <Separator />
            <div className="py-4 space-y-4">
              <h4 className="text-sm font-semibold text-foreground">Game Information</h4>
              <div className="grid gap-3">
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Date:</span>
                  <span className="text-foreground">{formattedDate}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Puck Drop:</span>
                  <span className="text-foreground font-bold">{game.time}</span>
                  <span className="text-muted-foreground text-xs">({userTimezone})</span>
                </div>
                {game.venue && (
                  <div className="flex items-center gap-3 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Venue:</span>
                    <span className="text-foreground">{game.venue}</span>
                  </div>
                )}
              </div>
            </div>

            {game.country && (
              <>
                <Separator />
                <div className="py-4 flex items-center gap-2 text-sm text-muted-foreground">
                  {game.countryFlag && (
                    <img src={game.countryFlag} alt={game.country} className="w-5 h-4 object-cover" />
                  )}
                  <span>{game.country}</span>
                  <span>•</span>
                  <span>{game.leagueName}</span>
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>

    </Sheet>

    <PlayerProfileModal
      playerName={selectedPlayerName}
      league={game?.league}
      gameContext={{
        league: game?.league,
        homeTeam: game?.homeTeam,
        awayTeam: game?.awayTeam,
        events: goalEvents,
      }}
      isOpen={!!selectedPlayerName}
      onClose={() => setSelectedPlayerName(null)}
    />
    </>
  );
};

export default GameDetailsModal;
